<template src="./index.html"></template>
<style src="./style.scss" lang="scss" scoped></style>
<script>
import { mapGetters } from 'vuex';

import Navbar from '@/components/Navbar';
import ProgressBar from '@/components/ProgressBar';
import InputField from '@/components/InputField';
import BackButton from '@/components/BackButton';
import CustomButton from '@/components/CustomButton';
import Background from '@/components/Background';
import GroupContainer from './components/GroupContainer';
import OnboardingGroupModal from './components/OnboardingGroupModal';
import CallFlowModal from '@/components/CallFlowModal';

import generateId from '@/utils/generate-id';
import { getAvatarColor } from '@/utils/colors';
import { mapGroups, mapTeamMembers } from '@/utils/formatUserGroups';
import {
  MUTATION_ADD_USER_TO_GROUP,
  MUTATION_CREATE_GROUP,
  MUTATION_DELETE_ALL_GROUPS,
  MUTATION_DELETE_ALL_USERS_FROM_ALL_GROUPS,
  MUTATION_DELETE_ALL_USERS_FROM_GROUP,
  MUTATION_DELETE_GROUP,
  MUTATION_DELETE_USER_FROM_GROUP,
  MUTATION_RENAME_GROUP,
  M_ONBOARDING_STEP
} from '@/graphql';

export default {
  name: 'CreateGroup',
  components: {
    Navbar,
    ProgressBar,
    InputField,
    BackButton,
    CustomButton,
    Background,
    GroupContainer,
    OnboardingGroupModal,
    CallFlowModal
  },
  data() {
    return {
      isLoading: false,
      loadingError: '',
      groups: [
        {
          error: '',
          id: generateId('group'),
          isCreated: false,
          members: [],
          name: ''
        }
      ],
      modalVisible: false,
      callflowModalVisible: false,
      withGroup: false
    };
  },
  computed: {
    ...mapGetters({
      user: 'auth/getUser',
      team: 'auth/getUserFullTeam',
      userGroups: 'auth/getUserGroups',
      numberName: 'onboarding/getSelectedName'
    }),
    addAnotherGroupEnabled() {
      return this.groups.filter((group) => group.members.length === 0).length === 0;
    },
    showDoLater() {
      return this.groups.filter((group) => !group.name && group.members.length === 0).length === this.groups.length;
    }
  },
  mounted() {
    // draw groups from Vuex
    if (this.userGroups.length > 0) {
      this.groups = [
        ...this.userGroups.map((group) => ({
          ...group,
          error: '',
          id: group.id || generateId('group')
        }))
      ];
    }
  },
  methods: {
    /**
     * Handle delayed group rename
     * @param {number|string|null} groupId - group ID
     * @param {string} name - group name
     * @returns {Promise<void>}
     */
    async handleDelayedRename(groupId = null, name = '') {
      try {
        return this.$apollo.mutate({
          mutation: MUTATION_RENAME_GROUP,
          variables: {
            groupId,
            name
          }
        });
      } catch (error) {
        return (this.groups = [
          ...this.groups.map(
            (item) =>
              (item.id === group.id && {
                ...item,
                error: 'Error renaming the group!'
              }) ||
              item
          )
        ]);
      }
    },

    /**
     * Handle group rename
     * @param {number|string|null} groupId - group ID
     * @param {string} name - group name
     * @returns {Promise<void>}
     */
    async handleRename(groupId = null, name = '') {
      this.groups = [
        ...this.groups.map(
          (item) =>
            (item.id === groupId && {
              ...item,
              name
            }) ||
            item
        )
      ];
      return await this.$store.dispatch('auth/setGroups', { groups: [...this.groups] });
    },

    /**
     * Add member to the group
     * @param {number|string|null} groupId - group ID
     * @param {number|null} userId - user ID
     * @returns {Promise<void>}
     */
    async addMember(groupId = null, userId = null) {
      const [group] = this.groups.filter(({ id = null }) => id === groupId);
      const [user] = this.team.filter(({ id = null }) => id === userId);

      // add user to the members list
      if (group.members.filter(({ id = null }) => id === user.id).length === 0) {
        this.groups = [
          ...this.groups.map(
            (item) =>
              (item.id === group.id && {
                ...item,
                members: [
                  ...item.members,
                  {
                    ...user,
                    avatarColor: user.avatarColor || getAvatarColor(),
                    avatarName: `${user.firstName[0]} ${user.lastName[0]}`,
                    isAttached: false,
                    name: `${user.firstName} ${user.lastName}`
                  }
                ]
              }) ||
              item
          )
        ];
        return await this.$store.dispatch('auth/setGroups', { groups: [...this.groups] });
      }
    },

    /**
     * Delete member from the group
     * @param {number|string|null} groupId - group ID
     * @param {number|null} userId - user ID
     * @returns {Promise<void>}
     */
    async deleteMember(groupId = null, userId = '') {
      const [group] = this.groups.filter(({ id = null }) => id === groupId);
      const [user] = group.members.filter((member) => member.id === userId);

      // remove user from the group on the backend
      if (group.isCreated && user.isAttached) {
        try {
          this.isLoading = true;
          await this.$apollo.mutate({
            mutation: MUTATION_DELETE_USER_FROM_GROUP,
            variables: {
              groupId,
              userId
            }
          });
          this.isLoading = false;
        } catch (error) {
          this.isLoading = false;
          return (this.groups = [
            ...this.groups.map(
              (item) =>
                (item.id === group.id && {
                  ...item,
                  error: 'Error deleting the member!'
                }) ||
                item
            )
          ]);
        }
      }

      // update the local store
      this.groups = [
        ...this.groups.map(
          (item) =>
            (item.id === group.id && {
              ...item,
              error: '',
              members: [...item.members.filter(({ id = null }) => id !== userId)]
            }) ||
            item
        )
      ];
      return await this.$store.dispatch('auth/setGroups', { groups: [...this.groups] });
    },

    /**
     * Delete group
     * @param {number|string|null} groupId - group ID
     * @returns {Promise<void>}
     */
    async deleteGroup(groupId = null) {
      const [group] = this.groups.filter(({ id = null }) => id === groupId);

      // delete group from the backend
      if (group.isCreated) {
        try {
          this.isLoading = true;
          await this.$apollo.mutate({
            mutation: MUTATION_DELETE_GROUP,
            variables: {
              groupId
            }
          });
          this.isLoading = false;
        } catch (error) {
          this.isLoading = false;
          return (this.groups = [
            ...this.groups.map(
              (item) =>
                (item.id === group.id && {
                  ...item,
                  error: `You can't delete this group, it's used in call or text routing.`
                }) ||
                item
            )
          ]);
        }
      }

      // update the local store
      this.groups = [...this.groups.filter(({ id = null }) => id !== groupId)];
      await this.$store.dispatch('auth/setGroups', { groups: [...this.groups] });
      await this.$store.dispatch('onboarding/setCallFlow', {});
    },

    /**
     * Create groups and attach the users
     * @returns {Promise<boolean|void>}
     */
    async onCreateGroups() {
      try {
        this.isLoading = true;

        // delete empty groups
        const clearedGroups = [];
        for (let i = 0; i < this.groups.length; i += 1) {
          if (this.groups[i].members.length === 0 || !this.groups[i].name) {
            if (this.groups[i].isCreated) {
              await this.$apollo.mutate({
                mutation: MUTATION_DELETE_GROUP,
                variables: {
                  groupId: this.groups[i].id
                }
              });
            }
          } else {
            clearedGroups.push(this.groups[i]);
          }
        }
        this.groups = clearedGroups;

        // create the groups
        const groupsToCreate = this.groups.reduce((arr, group) => {
          if (group.isCreated) {
            return arr;
          }
          arr.push({
            localId: group.id,
            name: group.name
          });
          return arr;
        }, []);
        const groupResults = await Promise.all(
          groupsToCreate.map(({ name = '' }) =>
            this.$apollo.mutate({
              mutation: MUTATION_CREATE_GROUP,
              variables: {
                name
              }
            })
          )
        );

        // update local groups
        const createdGroups = groupResults.map((result, index) => {
          const { data: { insert_groups: { returning = [] } = {} } = {} } = result;
          const [item = {}] = returning;
          return {
            id: item.id || null,
            localId: groupsToCreate[index].localId
          };
        });

        this.groups = [
          ...this.groups.map((group) => {
            const [createdGroup = null] = createdGroups.filter(({ localId = '' }) => localId === group.id);
            if (createdGroup) {
              return {
                ...group,
                id: createdGroup.id,
                isCreated: true,
                localId: createdGroup.localId
              };
            }
            return group;
          })
        ];

        // attach users
        const usersToAttach = this.groups.reduce((arr, group) => {
          if (group.members.length > 0) {
            group.members.forEach((user) => {
              if (!user.isAttached) {
                arr.push({
                  groupId: group.id,
                  userId: user.id
                });
                return arr;
              }
            });
          }
          return arr;
        }, []);
        await Promise.all(
          usersToAttach.map(({ groupId = null, userId = null }) =>
            this.$apollo.mutate({
              mutation: MUTATION_ADD_USER_TO_GROUP,
              variables: {
                groupId,
                userId
              }
            })
          )
        );

        // update local groups again
        this.groups = [
          ...this.groups.map((group) => {
            const targetUsers = usersToAttach.filter(({ groupId = null }) => groupId === group.id);
            if (targetUsers.length > 0) {
              return {
                ...group,
                members: [
                  ...group.members.map((user) => {
                    const [targetUser] = targetUsers.filter(({ userId = null }) => userId === user.id);
                    if (targetUser) {
                      return {
                        ...user,
                        isAttached: true
                      };
                    }
                    return user;
                  })
                ]
              };
            }
            return group;
          })
        ];

        await this.$store.dispatch('auth/setGroups', { groups: this.groups });

        await this.$apollo.mutate({
          mutation: M_ONBOARDING_STEP,
          variables: {
            user_id: this.user.id,
            onboarding_step: 3
          }
        });
        await this.$store.dispatch('onboarding/setOnboardingStep', 3);

        this.isLoading = false;
        return (this.modalVisible = true);
      } catch (error) {
        this.isLoading = false;
        console.log(error);
        return (this.groups = [
          ...this.groups.map((item) => ({
            ...item,
            error: 'Error saving the data!'
          }))
        ]);
      }
    },

    /**
     * If selecting to create the groups later
     * @returns {Promise<*>}
     */
    async onDoLater() {
      this.withGroup = false;

      // delete all groups
      try {
        this.isLoading = true;
        await this.$apollo.mutate({
          mutation: MUTATION_DELETE_ALL_USERS_FROM_ALL_GROUPS
        });
        await this.$apollo.mutate({
          mutation: MUTATION_DELETE_ALL_GROUPS
        });
        this.isLoading = false;
      } catch (error) {
        this.isLoading = false;
      }

      await this.$store.dispatch('auth/setGroups', { groups: [] });
      return (this.callflowModalVisible = true);
    },

    /**
     * Create another group
     * @returns {void}
     */
    onAddAnotherGroup() {
      this.groups.push({
        error: '',
        id: generateId('group'),
        isCreated: false,
        members: [],
        name: ''
      });
    },

    onNext() {
      this.withGroup = this.groups.length > 0;
      this.callflowModalVisible = true;
    },
    onRoute() {
      this.$router.push('/call-flow');
    }
  }
};
</script>
